<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirm, useToast } from "primevue";
import NotificationBanner from "@persolog/ui/NotificationBanner.vue";
import { formatDate } from "@persolog/utils";
import { LanguageManagementAPI } from "../api/index.ts";
import { Banner, BannerAudience, BannerState } from "../types/BannerTypes.ts";
import {
    BANNER_APPS,
    BANNER_LANGUAGES,
    bannerProblem,
    bannerState,
    emptyBanner,
    endFromPicker,
    displayAuthor,
    filledLanguages,
    formatWindowDate,
    pickerDate,
    plainText,
    REQUIRED_LANGUAGE,
    setBannerText,
    startFromPicker,
    textFor,
} from "../utils/bannerUtils.ts";

const { t } = useI18n();
const confirm = useConfirm();
const toast = useToast();

const banners = ref<Banner[]>([]);
const loading = ref(false);
const saving = ref(false);
const dialogOpen = ref(false);
const edited = ref<Banner>(emptyBanner());
const editedLanguage = ref(REQUIRED_LANGUAGE);

// The interface languages of the CUSTOMER-FACING frontends, not this app's own: the admin ships
// only de/en, but a banner has to be writable in every language a trainer or participant can pick.
const languages = BANNER_LANGUAGES;

const audiences: BannerAudience[] = ["ALL", "DE", "INT"];

const severityByState: Record<BannerState, string> = {
    active: "success",
    scheduled: "info",
    expired: "secondary",
    inactive: "warn",
};

const problem = computed(() => bannerProblem(edited.value));

// The preview runs the real component, so what the editor shows is what a trainer sees —
// including the markup, which is rendered with v-html.
const previewBanners = computed(() => {
    const text = textFor(edited.value, editedLanguage.value) || textFor(edited.value, REQUIRED_LANGUAGE);
    return text ? [{ id: 0, text, audience: edited.value.audience }] : [];
});

async function load() {
    loading.value = true;
    try {
        banners.value = await LanguageManagementAPI.getBanners();
    } catch (error) {
        console.error("Failed to load banners:", error);
        toast.add({ severity: "error", summary: t("banners.toast.loadFailed"), life: 5000 });
    } finally {
        loading.value = false;
    }
}

function openCreate() {
    edited.value = emptyBanner();
    editedLanguage.value = REQUIRED_LANGUAGE;
    dialogOpen.value = true;
}

function openEdit(banner: Banner) {
    // Deep-copy the texts too, so cancelling really discards the edit.
    edited.value = { ...banner, apps: [...banner.apps], texts: banner.texts.map((text) => ({ ...text })) };
    editedLanguage.value = REQUIRED_LANGUAGE;
    dialogOpen.value = true;
}

async function save() {
    if (problem.value) return;
    saving.value = true;
    try {
        if (edited.value.id === null) {
            await LanguageManagementAPI.createBanner(edited.value);
        } else {
            await LanguageManagementAPI.updateBanner(edited.value.id, edited.value);
        }
        dialogOpen.value = false;
        toast.add({ severity: "success", summary: t("banners.toast.saved"), life: 3000 });
        await load();
    } catch (error) {
        console.error("Failed to save banner:", error);
        toast.add({ severity: "error", summary: t("banners.toast.saveFailed"), life: 5000 });
    } finally {
        saving.value = false;
    }
}

function remove(banner: Banner) {
    confirm.require({
        message: t("banners.confirmDelete"),
        header: t("banners.delete"),
        icon: "pi pi-exclamation-triangle",
        rejectProps: { label: t("buttons.cancel"), severity: "secondary", outlined: true },
        acceptProps: { label: t("banners.delete"), severity: "danger" },
        accept: async () => {
            try {
                await LanguageManagementAPI.deleteBanner(banner.id as number);
                toast.add({ severity: "success", summary: t("banners.toast.deleted"), life: 3000 });
                await load();
            } catch (error) {
                console.error("Failed to delete banner:", error);
                toast.add({ severity: "error", summary: t("banners.toast.deleteFailed"), life: 5000 });
            }
        },
    });
}

/** Toggling active is the one action frequent enough to belong in the row rather than the dialog. */
async function toggleActive(banner: Banner) {
    try {
        await LanguageManagementAPI.updateBanner(banner.id as number, { ...banner, active: !banner.active });
        await load();
    } catch (error) {
        console.error("Failed to toggle banner:", error);
        toast.add({ severity: "error", summary: t("banners.toast.saveFailed"), life: 5000 });
        await load();
    }
}

const startDate = computed({
    get: () => pickerDate(edited.value.startsAt),
    set: (date: Date | null) => (edited.value.startsAt = startFromPicker(date)),
});

const endDate = computed({
    get: () => pickerDate(edited.value.endsAt),
    set: (date: Date | null) => (edited.value.endsAt = endFromPicker(date)),
});

const editedText = computed({
    get: () => textFor(edited.value, editedLanguage.value),
    // Mutates instead of reassigning `edited`: see setBannerText — replacing the object on every
    // keystroke re-rendered the dialog and dropped characters out of fast typing.
    set: (text: string) => setBannerText(edited.value, editedLanguage.value, text),
});

onMounted(load);
</script>

<template>
    <div class="p-4">
        <Toolbar class="mb-4">
            <template #start>
                <div>
                    <h1 class="text-2xl m-0">{{ t("banners.title") }}</h1>
                    <!-- Says out loud what the absence of a stage switch means, so nobody edits a
                         production banner in the wrong admin and wonders why nothing changed. -->
                    <small class="text-color-secondary">{{ t("banners.environmentHint") }}</small>
                </div>
            </template>
            <template #end>
                <Button
                    id="banners_btn_create"
                    data-cy="banner-create"
                    icon="pi pi-plus"
                    :label="t('banners.create')"
                    @click="openCreate"
                />
            </template>
        </Toolbar>

        <DataTable
            :value="banners"
            :loading="loading"
            data-key="id"
            data-cy="banner-table"
            striped-rows
            :empty-message="t('banners.empty')"
        >
            <!-- Grouped by the question each answers rather than one column per field: eight
                 columns made every row three lines tall and buried the text (RDT-339 review). -->
            <Column :header="t('banners.column.state')">
                <template #body="{ data }">
                    <div class="flex flex-col gap-1">
                        <Tag
                            :value="t(`banners.state.${bannerState(data)}`)"
                            :severity="severityByState[bannerState(data)]"
                            :data-cy="`banner-state-${data.id}`"
                            class="self-start"
                        />
                        <small class="text-color-secondary whitespace-nowrap" :data-cy="`banner-window-${data.id}`">
                            <template v-if="data.startsAt || data.endsAt">
                                {{ formatWindowDate(data.startsAt) }} – {{ formatWindowDate(data.endsAt) }}
                            </template>
                            <template v-else>{{ t("banners.window.always") }}</template>
                        </small>
                    </div>
                </template>
            </Column>
            <Column :header="t('banners.column.text')" class="w-2/5">
                <template #body="{ data }">
                    <div class="flex flex-col gap-1">
                        <!-- Tags stripped rather than rendered: markup in the overview would let one
                             banner restyle the table, and showing the raw tags is just unreadable. -->
                        <span class="banner-preview-text" :data-cy="`banner-text-${data.id}`">
                            {{ plainText(data, "de", "en") }}
                        </span>
                        <small class="text-color-secondary" :data-cy="`banner-languages-${data.id}`">
                            {{ filledLanguages(data).map((language) => language.toUpperCase()).join(", ") }}
                        </small>
                    </div>
                </template>
            </Column>
            <Column :header="t('banners.column.shownIn')">
                <template #body="{ data }">
                    <div class="flex flex-col gap-1" :data-cy="`banner-apps-${data.id}`">
                        <span v-if="data.apps.length === BANNER_APPS.length">{{ t("banners.allApps") }}</span>
                        <span v-else v-for="app in data.apps" :key="app">{{ t(`banners.app.${app}`) }}</span>
                        <small class="text-color-secondary">{{ t(`banners.audience.${data.audience}`) }}</small>
                    </div>
                </template>
            </Column>
            <Column :header="t('banners.column.updated')">
                <template #body="{ data }">
                    <div
                        v-if="data.updatedAt"
                        class="flex flex-col text-color-secondary text-sm"
                        :title="data.updatedBy ?? ''"
                        :data-cy="`banner-updated-${data.id}`"
                    >
                        <span>{{ displayAuthor(data.updatedBy) }}</span>
                        <span>{{ formatDate(data.updatedAt) }}</span>
                    </div>
                </template>
            </Column>
            <Column :header="t('banners.column.actions')">
                <template #body="{ data }">
                    <div class="flex items-center gap-2">
                        <ToggleSwitch
                            :model-value="data.active"
                            :data-cy="`banner-toggle-${data.id}`"
                            :aria-label="t('banners.active')"
                            @update:model-value="toggleActive(data)"
                        />
                        <Button
                            icon="pi pi-pencil"
                            text
                            :aria-label="t('banners.edit')"
                            :data-cy="`banner-edit-${data.id}`"
                            @click="openEdit(data)"
                        />
                        <Button
                            icon="pi pi-trash"
                            text
                            severity="danger"
                            :aria-label="t('banners.delete')"
                            :data-cy="`banner-delete-${data.id}`"
                            @click="remove(data)"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <!-- Each view that confirms renders its own: ConfirmDialog is not global the way Toast is
             in App.vue, so without this the delete button silently did nothing (RDT-339). -->
        <ConfirmDialog />

        <Dialog
            v-model:visible="dialogOpen"
            modal
            :header="edited.id === null ? t('banners.create') : t('banners.edit')"
            :style="{ width: '56rem' }"
            data-cy="banner-dialog"
        >
            <div class="flex flex-col gap-5">
                <!-- What the banner says comes first: it is the thing being written, and the
                     settings only qualify it (RDT-339 review). -->
                <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-3 flex-wrap">
                        <label for="banners_select_language" class="font-semibold">{{ t("banners.language") }}</label>
                        <Select
                            id="banners_select_language"
                            v-model="editedLanguage"
                            :options="languages"
                            data-cy="banner-language"
                        >
                            <template #value="{ value }">
                                {{ value?.toUpperCase() }}<span v-if="value === REQUIRED_LANGUAGE"> *</span>
                            </template>
                            <template #option="{ option }">
                                {{ option.toUpperCase() }}
                                <i v-if="filledLanguages(edited).includes(option)" class="pi pi-check ml-2 text-xs" />
                            </template>
                        </Select>
                        <small class="text-color-secondary">{{ t("banners.languageHint") }}</small>
                    </div>
                    <!-- autofocus so the dialog's focus lands in the text field. Without it the
                         Dialog focuses its first focusable element once the open transition ends —
                         the close button — and anything typed before that lands on the button
                         instead, where a space closes the dialog (RDT-339). -->
                    <Textarea
                        id="banners_input_text"
                        v-model="editedText"
                        rows="3"
                        auto-resize
                        autofocus
                        data-cy="banner-text-input"
                    />
                    <small class="text-color-secondary">{{ t("banners.htmlHint") }}</small>
                </div>

                <div class="flex flex-col gap-2">
                    <span class="font-semibold">{{ t("banners.preview") }}</span>
                    <!-- Rendered with the audience it is addressed to, so the preview is never
                         empty just because the editor's own locale is the other market. -->
                    <div data-cy="banner-preview">
                        <NotificationBanner
                            :banners="previewBanners"
                            :locale="edited.audience === 'DE' ? 'de' : 'en'"
                        />
                    </div>
                </div>

                <!-- Grouped by the question each answers - where does it show, and when - rather
                     than one flat list of controls (RDT-339 review). -->
                <Fieldset :legend="t('banners.whereLegend')">
                    <div class="flex flex-col gap-3">
                        <fieldset class="banner-checkgroup">
                            <legend class="text-color-secondary text-sm">{{ t("banners.column.apps") }}</legend>
                            <div class="flex flex-wrap gap-4">
                                <div v-for="app in BANNER_APPS" :key="app" class="flex items-center gap-2">
                                    <Checkbox
                                        v-model="edited.apps"
                                        :input-id="`banners_check_${app}`"
                                        :value="app"
                                        :data-cy="`banner-app-${app}`"
                                    />
                                    <label :for="`banners_check_${app}`">{{ t(`banners.app.${app}`) }}</label>
                                </div>
                            </div>
                        </fieldset>

                        <div class="flex flex-col gap-2">
                            <label for="banners_select_audience" class="text-color-secondary text-sm">
                                {{ t("banners.column.audience") }}
                            </label>
                            <SelectButton
                                id="banners_select_audience"
                                v-model="edited.audience"
                                :options="audiences"
                                :option-label="(audience: BannerAudience) => t(`banners.audience.${audience}`)"
                                :allow-empty="false"
                                class="self-start"
                                data-cy="banner-audience"
                            />
                            <small class="text-color-secondary">{{ t("banners.audienceHint") }}</small>
                        </div>
                    </div>
                </Fieldset>

                <Fieldset :legend="t('banners.whenLegend')">
                    <div class="flex flex-col gap-3">
                        <div class="flex items-center gap-3">
                            <ToggleSwitch id="banners_switch_active" v-model="edited.active" data-cy="banner-active" />
                            <label for="banners_switch_active">{{ t("banners.active") }}</label>
                            <small class="text-color-secondary">{{ t("banners.activeHint") }}</small>
                        </div>

                        <div class="flex flex-wrap gap-4">
                            <div class="flex flex-col gap-2">
                                <label for="banners_date_start" class="text-color-secondary text-sm">
                                    {{ t("banners.startsAt") }}
                                </label>
                                <DatePicker
                                    id="banners_date_start"
                                    v-model="startDate"
                                    date-format="dd.mm.yy"
                                    show-button-bar
                                    data-cy="banner-start"
                                />
                            </div>
                            <div class="flex flex-col gap-2">
                                <label for="banners_date_end" class="text-color-secondary text-sm">
                                    {{ t("banners.endsAt") }}
                                </label>
                                <DatePicker
                                    id="banners_date_end"
                                    v-model="endDate"
                                    date-format="dd.mm.yy"
                                    show-button-bar
                                    data-cy="banner-end"
                                />
                            </div>
                        </div>
                        <small class="text-color-secondary">{{ t("banners.windowHint") }}</small>
                    </div>
                </Fieldset>

                <Message v-if="problem" severity="error" :closable="false" data-cy="banner-problem">
                    {{ t(problem) }}
                </Message>
            </div>

            <template #footer>
                <Button :label="t('buttons.cancel')" severity="secondary" text @click="dialogOpen = false" />
                <Button
                    :label="t('buttons.save')"
                    :disabled="problem !== null || saving"
                    :loading="saving"
                    data-cy="banner-save"
                    @click="save"
                />
            </template>
        </Dialog>
    </div>
</template>

<style scoped>
/* A bare grouping fieldset: it exists to give the checkboxes an accessible name, not to draw a box. */
.banner-checkgroup {
    border: 0;
    padding: 0;
    margin: 0;
}

.banner-checkgroup legend {
    padding: 0;
    margin-bottom: 0.5rem;
}

.banner-preview-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 28rem;
}
</style>
